import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dailyGrowthCommandCenterApi,
  type AcceptanceCriterion,
  type AIWorkspaceSection,
  type APISpecification,
  type BusinessObjective,
  type CommandCenterDashboard,
  type CommandCenterValidation,
  type DatabaseTable,
  type DGCCPRDStatus,
  type ExecutiveCard,
  type LayoutRegion,
  type LiveUpdateChannel,
  type OpportunityInboxItem,
  type PerformanceTarget,
  type QuickAction,
  type TestingRequirement,
  type WorkspacePhilosophyQuestion,
} from '../../api/daily-growth-command-center-prd';

type Tab = 'overview' | 'philosophy' | 'layout' | 'inbox' | 'cards' | 'actions' | 'engineering' | 'status';
type Theme = 'light' | 'dark';

const riskColors: Record<string, string> = {
  Low: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  Medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  High: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export function DailyGrowthCommandCenterPRDPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<CommandCenterDashboard | null>(null);
  const [objectives, setObjectives] = useState<BusinessObjective[]>([]);
  const [philosophy, setPhilosophy] = useState<WorkspacePhilosophyQuestion[]>([]);
  const [layout, setLayout] = useState<LayoutRegion[]>([]);
  const [inbox, setInbox] = useState<OpportunityInboxItem[]>([]);
  const [cards, setCards] = useState<ExecutiveCard[]>([]);
  const [aiSections, setAiSections] = useState<AIWorkspaceSection[]>([]);
  const [actions, setActions] = useState<QuickAction[]>([]);
  const [liveChannels, setLiveChannels] = useState<LiveUpdateChannel[]>([]);
  const [perf, setPerf] = useState<PerformanceTarget[]>([]);
  const [apis, setApis] = useState<APISpecification[]>([]);
  const [tables, setTables] = useState<DatabaseTable[]>([]);
  const [testing, setTesting] = useState<TestingRequirement[]>([]);
  const [acceptance, setAcceptance] = useState<AcceptanceCriterion[]>([]);
  const [status, setStatus] = useState<DGCCPRDStatus | null>(null);
  const [validation, setValidation] = useState<CommandCenterValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, obj, phil, lay, opp, crd, ai, act, live, pf, api, tbl, tst, acc, st] = await Promise.all([
        dailyGrowthCommandCenterApi.dashboard(),
        dailyGrowthCommandCenterApi.businessObjectives(),
        dailyGrowthCommandCenterApi.workspacePhilosophy(),
        dailyGrowthCommandCenterApi.layout(),
        dailyGrowthCommandCenterApi.opportunityInbox(),
        dailyGrowthCommandCenterApi.executiveCards(),
        dailyGrowthCommandCenterApi.aiWorkspace(),
        dailyGrowthCommandCenterApi.quickActions(),
        dailyGrowthCommandCenterApi.liveUpdates(),
        dailyGrowthCommandCenterApi.performanceTargets(),
        dailyGrowthCommandCenterApi.apis(),
        dailyGrowthCommandCenterApi.databaseSchema(),
        dailyGrowthCommandCenterApi.testing(),
        dailyGrowthCommandCenterApi.acceptanceCriteria(),
        dailyGrowthCommandCenterApi.status(),
      ]);
      setDashboard(dash);
      setObjectives(obj);
      setPhilosophy(phil);
      setLayout(lay);
      setInbox(opp);
      setCards(crd);
      setAiSections(ai);
      setActions(act);
      setLiveChannels(live);
      setPerf(pf);
      setApis(api);
      setTables(tbl);
      setTesting(tst);
      setAcceptance(acc);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load command center PRD');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await dailyGrowthCommandCenterApi.validate({
        attrs: {
          layout_complete: true, opportunity_inbox: true, performance_met: true,
          ai_workspace_ready: true, acceptance_passed: true,
        },
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Command center PRD passes production readiness gate' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-rose-900/40 bg-slate-900' : 'border-rose-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-rose-50/20 text-slate-900';
  const accent = dark ? 'text-rose-300' : 'text-rose-700';
  const tabActive = 'bg-rose-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'philosophy', label: 'Philosophy' },
    { id: 'layout', label: 'Layout' },
    { id: 'inbox', label: 'Opportunity Inbox' },
    { id: 'cards', label: 'Executive Cards' },
    { id: 'actions', label: 'Actions & AI' },
    { id: 'engineering', label: 'Engineering' },
    { id: 'status', label: 'Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>PRD-000002</p>
          <h1 className="text-2xl font-semibold">Daily Growth Command Center</h1>
          <p className={`text-sm ${muted}`}>
            Marketplace Operating System home — executive workspace, AI command center, opportunity inbox and daily decision engine
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-intelligence-v1-prd" className={`${accent} hover:underline`}>PRD-000001</Link>
            <span className={muted}>→</span>
            <Link to="/platform/marketplace-mos-bible" className={`${accent} hover:underline`}>MIB-000005 MOS</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Primary Daily Workspace</h2>
            <p className={`mt-1 text-sm ${muted}`}>Depends on: {dashboard.depends_on.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              Proactively surfaces highest value opportunities, risks, ROI actions, and AI recommendations.
              Seller understands business health, priorities, and expected impact within 5 seconds of login.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Layout Regions', value: dashboard.layout_regions },
              { label: 'Opportunities', value: dashboard.opportunity_inbox_items },
              { label: 'Executive Cards', value: dashboard.executive_cards },
              { label: 'Quick Actions', value: dashboard.quick_actions },
              { label: 'Live Channels', value: dashboard.live_update_channels },
              { label: 'DB Tables', value: dashboard.database_tables },
              { label: 'AI Capabilities', value: dashboard.ai_capabilities },
              { label: 'Acceptance Criteria', value: dashboard.acceptance_criteria },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Business Objectives</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {objectives.map((o) => (
                <div key={o.key} className="rounded-lg border border-rose-200 p-3 dark:border-rose-800">
                  <span className="font-medium">{o.name}</span>
                  <p className={`mt-1 text-xs ${muted}`}>{o.description}</p>
                  {o.kpi && <span className="mt-1 inline-block rounded bg-rose-100 px-2 py-0.5 text-xs dark:bg-rose-900">{o.kpi}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'philosophy' && (
        <div className="space-y-3">
          {philosophy.map((q) => (
            <div key={q.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="text-lg font-semibold">{q.question}</h3>
              {q.answered_by && <p className={`mt-1 text-sm ${muted}`}>Answered by: {q.answered_by}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'layout' && (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {layout.sort((a, b) => a.order - b.order).map((r) => (
            <div key={r.key} className={`rounded-xl border p-4 ${card}`}>
              <span className="text-xs text-rose-600 dark:text-rose-400">{r.order}</span>
              <h3 className="font-semibold">{r.name}</h3>
              <p className={`text-xs ${muted}`}>{r.region} · {r.component}</p>
              {r.description && <p className={`mt-1 text-sm ${muted}`}>{r.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'inbox' && (
        <div className="space-y-3">
          {inbox.sort((a, b) => b.priority_score - a.priority_score).map((o) => (
            <div key={o.key} className={`rounded-xl border p-5 ${card}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h3 className="font-semibold">{o.title}</h3>
                <div className="flex gap-2">
                  <span className="rounded bg-rose-100 px-2 py-0.5 text-xs font-medium dark:bg-rose-900">Score {o.priority_score.toFixed(1)}</span>
                  <span className={`rounded px-2 py-0.5 text-xs ${riskColors[o.risk] ?? ''}`}>{o.risk}</span>
                </div>
              </div>
              <p className={`mt-2 text-sm ${muted}`}>{o.business_problem}</p>
              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                <span>Revenue: <strong>{o.expected_revenue}</strong></span>
                <span>Profit: <strong>{o.expected_profit}</strong></span>
                <span>Confidence: <strong>{(o.confidence * 100).toFixed(0)}%</strong></span>
                <span>Effort: {o.estimated_effort} · {o.required_time}</span>
              </div>
              <button type="button" className={`mt-3 rounded-lg px-4 py-1.5 text-sm text-white ${tabActive}`}>{o.one_click_action}</button>
            </div>
          ))}
        </div>
      )}

      {tab === 'cards' && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => (
            <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{c.name}</h3>
                {c.actionable && <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs dark:bg-emerald-900">Actionable</span>}
              </div>
              {c.metric && <p className={`mt-1 text-xs ${muted}`}>{c.metric} ({c.unit})</p>}
              {c.alert_threshold && <p className={`text-xs ${muted}`}>Alert: {c.alert_threshold}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'actions' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Quick Actions</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {actions.map((a) => (
                <div key={a.key} className={`flex items-center gap-4 rounded-lg border px-4 py-3 ${card}`}>
                  <span className="font-medium">{a.name}</span>
                  {a.shortcut && <code className="rounded bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">{a.shortcut}</code>}
                  <span className={`ml-auto text-xs ${muted}`}>{a.target_latency}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">AI Workspace</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {aiSections.map((s) => (
                <div key={s.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{s.name}</h3>
                    {s.persistent && <span className="rounded bg-rose-100 px-2 py-0.5 text-xs dark:bg-rose-900">Persistent</span>}
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>{s.description}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Live Update Channels</h2>
            <div className="space-y-2">
              {liveChannels.map((c) => (
                <div key={c.key} className={`rounded-lg border px-4 py-2 ${card}`}>
                  <span className="font-medium">{c.name}</span>
                  <span className={`ml-3 text-xs ${muted}`}>{c.target_latency}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'engineering' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Performance Targets</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {perf.map((p) => (
                <div key={p.key} className={`rounded-lg border px-4 py-3 ${card}`}>
                  <span className="font-medium">{p.surface}</span>
                  <span className={`ml-2 text-lg font-semibold text-rose-600 dark:text-rose-400`}>{p.max_value}{p.unit !== 'seconds' && p.unit !== 'ms' ? '' : ` ${p.unit}`}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">APIs</h2>
            {apis.map((a) => (
              <div key={a.key} className={`mb-3 rounded-xl border p-4 ${card}`}>
                <h3 className="font-semibold capitalize">{a.category}: {a.key}</h3>
                <p className={`text-sm ${muted}`}>{a.description}</p>
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {a.endpoints?.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </div>
            ))}
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Database Schema</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {tables.map((t) => (
                <div key={t.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{t.name}</h3>
                  <p className={`text-sm ${muted}`}>{t.purpose}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Testing & Acceptance</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                {testing.map((t) => (
                  <div key={t.key} className={`mb-2 rounded-lg border px-4 py-2 ${card}`}>
                    <span className="font-medium capitalize">{t.level}</span>
                    <span className={`ml-2 text-xs ${muted}`}>{t.scope}</span>
                  </div>
                ))}
              </div>
              <div>
                {acceptance.map((a) => (
                  <div key={a.key} className={`mb-2 rounded-lg border px-4 py-2 ${card}`}>
                    <p className="text-sm">{a.criterion}</p>
                    {a.target && <p className={`text-xs ${muted}`}>Target: {a.target}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">{status.status}</span>
            {status.default_landing && <p className={`mt-3 text-sm ${muted}`}>Default landing experience after login</p>}
            {status.production_ready && <p className={`text-sm ${muted}`}>Production implementation specification complete</p>}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate Command Center PRD</h3>
            <button type="button" disabled={busy} onClick={validate} className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}>
              {busy ? 'Validating…' : 'Run Readiness Validation'}
            </button>
            {validation && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">{JSON.stringify(validation, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
